import React from 'react';
import { Marker, Popup, Circle } from 'react-leaflet';
import L from 'leaflet';
import { useMap } from '../../../contexts/MapContext';
import { transformCoordinate } from '../../../utils/coordinateTransform';

const hospitalIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjQ0MwMDAwIj48cGF0aCBkPSJNMTkgOGgtNFY0SDlodjRINXYyaDR2NGgydi00aDR6Ii8+PC9zdmc+',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
});

const HealthFacilitiesLayer: React.FC = () => {
  const { gazaPosition } = useMap();
  
  // Real health facility locations in Gaza
  const facilitiesOriginal = [
    { id: 1, name: 'Al-Shifa Hospital', lat: 31.5245, lng: 34.4444, status: 'Limited Services', beds: 50, type: 'Main Hospital' },
    { id: 2, name: 'Al-Quds Hospital', lat: 31.5156, lng: 34.4389, status: 'Critical', beds: 10, type: 'Hospital' },
    { id: 3, name: 'Nasser Medical Complex', lat: 31.3444, lng: 34.3056, status: 'Operational', beds: 100, type: 'Medical Complex' },
    { id: 4, name: 'European Gaza Hospital', lat: 31.2950, lng: 34.2661, status: 'Limited Services', beds: 30, type: 'Hospital' },
    { id: 5, name: 'Indonesian Hospital', lat: 31.5683, lng: 34.5017, status: 'Critical', beds: 15, type: 'Hospital' },
    { id: 6, name: 'Kamal Adwan Hospital', lat: 31.5578, lng: 34.5006, status: 'Damaged', beds: 0, type: 'Hospital' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Operational': return '#4CAF50';
      case 'Limited Services': return '#FF9800';
      case 'Critical': return '#F44336';
      case 'Damaged': return '#9E9E9E';
      default: return '#757575';
    }
  };

  // Transform coordinates to match overlay position
  const transformedFacilities = facilitiesOriginal.map(facility => {
    const transformed = transformCoordinate(facility.lat, facility.lng, gazaPosition);
    if (!transformed) return null;
    
    return {
      ...facility,
      transformedLat: transformed[0],
      transformedLng: transformed[1],
    };
  }).filter(Boolean);

  if (!gazaPosition) return null;

  return (
    <>
      {transformedFacilities.map((facility) => (
        facility && (
          <React.Fragment key={facility.id}>
            <Marker
              position={[facility.transformedLat, facility.transformedLng]}
              icon={hospitalIcon}
            >
              <Popup>
                <div style={{ minWidth: '180px' }}>
                  <strong>{facility.name}</strong>
                  <br />
                  <span style={{ fontSize: '12px', color: '#666' }}>{facility.type}</span>
                  <br />
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '4px 8px', 
                    backgroundColor: getStatusColor(facility.status) + '20',
                    borderLeft: `3px solid ${getStatusColor(facility.status)}`,
                  }}>
                    <strong>Status:</strong> {facility.status}
                    <br />
                    <strong>Available Beds:</strong> {facility.beds}
                  </div>
                  <small style={{ color: '#999', display: 'block', marginTop: '8px' }}>
                    Healthcare access severely limited
                  </small>
                </div>
              </Popup>
            </Marker>
            {facility.status === 'Operational' && (
              <Circle
                center={[facility.transformedLat, facility.transformedLng]}
                radius={1000} // 1km service radius
                pathOptions={{
                  color: getStatusColor(facility.status),
                  fillColor: getStatusColor(facility.status),
                  fillOpacity: 0.1,
                  weight: 1,
                  dashArray: '3, 6',
                }}
              />
            )}
          </React.Fragment>
        )
      ))}
    </>
  );
};

export default HealthFacilitiesLayer;